import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Upload, Building } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

const generalSettingsSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  slug: z.string().min(3, 'Slug must be at least 3 characters').regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  description: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
  logo: z.string().optional(),
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  timezone: z.string(),
  dateFormat: z.string(),
  publicProfile: z.boolean(),
});

type GeneralSettingsFormData = z.infer<typeof generalSettingsSchema>;

export function GeneralSettings() {
  const { organization } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<GeneralSettingsFormData>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: {
      name: organization?.name || '',
      slug: organization?.slug || '',
      description: '',
      website: '',
      logo: organization?.branding?.logo || '',
      primaryColor: organization?.branding?.primaryColor || '#000000',
      secondaryColor: organization?.branding?.secondaryColor || '#666666',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      publicProfile: false,
    },
  });

  const onSubmit = async (data: GeneralSettingsFormData) => {
    try {
      // In a real app, this would call tenantAPI.updateTenant
      console.log('Updating organization settings:', data);
      
      toast({
        title: 'Settings updated',
        description: 'Organization settings have been saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update organization settings.',
        variant: 'destructive',
      });
    }
  };

  const handleLogoUpload = () => {
    // In a real app, this would handle file upload
    toast({
      title: 'Feature coming soon',
      description: 'Logo upload functionality will be available soon.',
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization Profile</CardTitle>
          <CardDescription>
            Basic information about your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>Organization Logo</Label>
              <div className="flex items-center space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={watch('logo')} />
                  <AvatarFallback>
                    <Building className="h-8 w-8" />
                  </AvatarFallback>
                </Avatar>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleLogoUpload}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Logo
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="Enter organization name"
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">
                    sixsight.app/
                  </span>
                  <Input
                    id="slug"
                    {...register('slug')}
                    placeholder="organization-name"
                    className="rounded-l-none"
                  />
                </div>
                {errors.slug && (
                  <p className="text-sm text-red-600">{errors.slug.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                {...register('description')}
                placeholder="Brief description of your organization"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                {...register('website')}
                placeholder="https://www.yourwebsite.com"
              />
              {errors.website && (
                <p className="text-sm text-red-600">{errors.website.message}</p>
              )}
            </div>

            {/* Plan Information */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium mb-4">Subscription Plan</h3>
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="default" className="text-sm">
                    {organization?.plan || 'FREE'}
                  </Badge>
                  <p className="text-sm text-muted-foreground mt-1">
                    Current plan status: {organization?.status || 'ACTIVE'}
                  </p>
                </div>
                <Button variant="outline">
                  Manage Plan
                </Button>
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <CardDescription>
            Customize the appearance of your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex space-x-2">
                <Input
                  id="primaryColor"
                  type="color"
                  {...register('primaryColor')}
                  className="w-16 h-10"
                />
                <Input
                  {...register('primaryColor')}
                  placeholder="#000000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex space-x-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  {...register('secondaryColor')}
                  className="w-16 h-10"
                />
                <Input
                  {...register('secondaryColor')}
                  placeholder="#666666"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>
            Configure organization-wide preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Public Profile</Label>
                <p className="text-sm text-muted-foreground">
                  Allow your organization to be discoverable publicly
                </p>
              </div>
              <Switch
                checked={watch('publicProfile')}
                onCheckedChange={(checked) => setValue('publicProfile', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}